import PropTypes from 'prop-types';
import { Form, useFormik, FormikProvider } from 'formik';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  Typography,
} from '@mui/material';
import theme from 'src/theme';
import Loader from 'src/common/Loader';
import {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveCardsList, getAllCards } from 'src/redux/cardSlice';
import { requestCardLimitChange, getCardApprovalsList } from 'src/redux/cardApprovalSlice';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import FMSModal from 'src/common/FMSModal';
import InputField from 'src/common/formFields/InputField';
import AutocompleteField from 'src/common/formFields/AutocompleteField';
import { getVehicleFuelLevel } from 'src/redux/vehicleSlice';
import {
  cardApprovalsFormModel,
  cardApprovalFormInitialValues,
  cardApprovalsValidationSchema
} from './cardApprovalsFormModel';

const NewCardApproval = ({ open, handleClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [disableFuelLevel, setDisableFuelLevel] = useState(true);
  const lastFuelFetchRef = useRef({ cardId: null, vehicleId: null });
  const allActiveCards = useSelector(getActiveCardsList);
  const vehicleOptions = useMemo(() => (
    allActiveCards
      .filter((card) => card.vehicle_number)
      .reduce((vehicles, card) => {
        if (!vehicles.some((vehicle) => vehicle.id === card.vehicle_number)) {
          vehicles.push({
            id: card.vehicle_number,
            registration_num: card.vehicle_number,
          });
        }
        return vehicles;
      }, [])
  ), [allActiveCards]);
  const requiredHintText = '';

  const {
    formId,
    formField: {
      vehicleNumber,
      cardName,
      tankCapacity,
      tankFuelPercentage,
      requestedQuantity,
      odometerEntry
    }
  } = cardApprovalsFormModel;

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      const payload = {
        card_id: values.card_id,
        vehicle_number: values.vehicle_number?.trim(),
        tank_capacity: values.tank_capacity,
        requested_quantity: Number(values.requested_quantity),
        tank_fuel_percentage: Number(values.tank_fuel_percentage),
        odometer_entry: values.odometer_entry !== '' && values.odometer_entry !== null && values.odometer_entry !== undefined
          ? Number(values.odometer_entry)
          : null,
      };
      await dispatch(requestCardLimitChange(payload)).unwrap();
      handleClose();
      setLoading(false);
      dispatch(
        setSuccessInfo({
          successMsg: 'Card Limit update request sent successfully!'
        })
      );
      // reload approvals list
      dispatch(getCardApprovalsList({ limit: 10 }));
    } catch (error) {
      console.log('error', error);
      // handle error here
      actions.setSubmitting(false);
      setLoading(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    }
  };

  const formikProps = useFormik({
    initialValues: cardApprovalFormInitialValues,
    validationSchema: cardApprovalsValidationSchema,
    onSubmit,
  });
  const selectedVehicleNumber = formikProps.values.vehicle_number;
  const cardOptions = useMemo(() => (
    selectedVehicleNumber
      ? allActiveCards.filter((card) => card.vehicle_number === selectedVehicleNumber)
      : allActiveCards
  ), [allActiveCards, selectedVehicleNumber]);

  useEffect(() => {
    if (open) {
      formikProps.resetForm();
      setFormError(null);
      setDisableFuelLevel(true);
      lastFuelFetchRef.current = { cardId: null, vehicleId: null };
      dispatch(getAllCards());
    }
  }, [dispatch, open]);

  const fetchVehicleFuelLevelStatus = async (vehicleId) => {
    try {
      setFormLoading(true);
      const res = await dispatch(getVehicleFuelLevel(vehicleId)).unwrap();
      if (res?.fuel_level !== undefined && res?.fuel_level !== null) {
        formikProps.setFieldValue(tankFuelPercentage.name, res.fuel_level);
        setDisableFuelLevel(true);
      }
    } catch (error) {
      const errorMsg = error?.error_message
        ? `${error.error_message}. Enter fuel level manually or try again!`
        : 'Unable to fetch fuel level from sensor. Enter fuel level manually or try again!';
      setFormError(errorMsg);
      formikProps.setFieldValue(tankFuelPercentage.name, 0);
      setDisableFuelLevel(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCardSelection = (cardId) => {
    if (!cardId) {
      setFormError(null);
      setDisableFuelLevel(true);
      lastFuelFetchRef.current = { cardId: null, vehicleId: null };
      formikProps.setFieldValue(vehicleNumber.name, '');
      formikProps.setFieldValue(tankCapacity.name, '');
      formikProps.setFieldValue(tankFuelPercentage.name, '');
      return;
    }

    const cardDetails = allActiveCards.find((card) => card.id === cardId);
    if (!cardDetails) {
      return;
    }

    setFormError(null);
    setDisableFuelLevel(true);
    formikProps.setFieldValue(vehicleNumber.name, cardDetails.vehicle_number || '');
    formikProps.setFieldValue(tankCapacity.name, cardDetails.tank_capacity || '');

    if (cardDetails.vehicle_id && cardDetails.telemetics_enabled) {
      const isDuplicateFuelRequest = (
        lastFuelFetchRef.current.cardId === cardId
        && lastFuelFetchRef.current.vehicleId === cardDetails.vehicle_id
      );
      if (isDuplicateFuelRequest) {
        return;
      }
      lastFuelFetchRef.current = { cardId, vehicleId: cardDetails.vehicle_id };
      fetchVehicleFuelLevelStatus(cardDetails.vehicle_id);
    } else {
      lastFuelFetchRef.current = { cardId: null, vehicleId: null };
      formikProps.setFieldValue(tankFuelPercentage.name, 0);
      setDisableFuelLevel(false);
    }
  };

  const handleVehicleSelection = (vehicle) => {
    const vehicleNumberValue = vehicle?.registration_num || '';
    formikProps.setFieldValue(vehicleNumber.name, vehicleNumberValue);

    const firstCard = allActiveCards.find((card) => card.vehicle_number === vehicleNumberValue);
    formikProps.setFieldValue(cardName.name, firstCard?.id || '');
  };

  useEffect(() => {
    if (!open) return;
    handleCardSelection(formikProps.values.card_id);
  }, [open, formikProps.values.card_id]);

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontSize: 18,
          background: theme.palette.grey[800],
          color: theme.palette.common.white
        }}
      >
        Request Card Limit
      </DialogTitle>
      <Divider />
      <FormikProvider value={formikProps}>
        {loading || formLoading ? (
          <Loader />
        ) : (
          <Form id={formId} onSubmit={formikProps.handleSubmit}>
            <DialogContent>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                * Required fields
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <AutocompleteField
                    name={vehicleNumber.name}
                    label={`${vehicleNumber.label} *`}
                    options={vehicleOptions}
                    getOptionLabel={(option) => option.registration_num || ''}
                    placeholder="Search vehicle number"
                    onSelectionChange={handleVehicleSelection}
                    required
                    requiredHintText={requiredHintText}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AutocompleteField
                    name={cardName.name}
                    label={`${cardName.label} *`}
                    options={cardOptions}
                    getOptionLabel={(option) => option.uid || ''}
                    onSelectionChange={(card) => handleCardSelection(card?.id)}
                    required
                    requiredHintText={requiredHintText}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={tankCapacity.name}
                    label={tankCapacity.label}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={tankFuelPercentage.name}
                    label={`${tankFuelPercentage.label} *`}
                    InputProps={{
                      endAdornment: (<InputAdornment position="end">%</InputAdornment>),
                    }}
                    fullWidth
                    disabled={disableFuelLevel}
                    required
                    requiredHintText={requiredHintText}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={requestedQuantity.name}
                    label={`${requestedQuantity.label} *`}
                    required
                    requiredHintText={requiredHintText}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={odometerEntry.name}
                    label={odometerEntry.label}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </DialogContent>
            {formError && <Typography sx={{ textAlign: 'center' }} variant="subtitle1" color="error">{formError}</Typography>}
            <DialogActions sx={{ p: 2 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleClose}
                disabled={formikProps.isSubmitting || formLoading}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={formikProps.isSubmitting || formLoading}
                type="submit"
                size="large"
                variant="contained"
              >
                Submit
              </Button>
            </DialogActions>
          </Form>
        )}
      </FormikProvider>
    </FMSModal>
  );
};

NewCardApproval.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default NewCardApproval;
